import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Slider,
  Switch,
  FormControlLabel,
  Button,
  Autocomplete,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  Map as MapIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Pentagon as PentagonIcon,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { WorkflowConditionType, RouteConfig, WorkflowNodeType } from '@features/adventures/types/workflow.types'
import { useWorkflowStore } from '@store/workflowStore'
import { useAdventureEditorStore } from '@features/adventures/stores/adventureEditorStore'
import { TagType } from '@features/adventures/types/adventure-editor.types'
import { MeasurementUnit } from '@/types/settings'
import { ApiService } from '@api/apiService'
import { searchApi } from '@api/endpoints/search'
import { SearchEntityType, type CollectionSearchResult, type AdventureSearchResult } from '@api/types/search'
import { GeofencePicker, type ContextMarker, type ContextRoute } from '@components/map/GeofencePicker'
import { useCachedTags } from '@hooks/useCachedTags'
import weatherConditions from './WeatherConditions.json'
import { TimeScheduleConfigPanel } from './TimeScheduleConfigPanel'

// User Rank definitions - matches backend rank system
interface RankDefinition {
  tier: number
  name: string
  badge: string
  pointsRequired: number
  theme: string
  description: string
}

const USER_RANKS: RankDefinition[] = [
  { tier: 1, name: 'Lamplighter', badge: '🕯️', pointsRequired: 0, theme: 'Historical / Urbex', description: 'The first to wander dim corridors and forgotten streets. Lights the path.' },
  { tier: 2, name: 'Scoutwalker', badge: '🥾', pointsRequired: 100, theme: 'Hiking / Exploration', description: 'Navigates entry-level trails and city paths with ease. A vigilant observer.' },
  { tier: 3, name: 'Keyfinder', badge: '🔑', pointsRequired: 300, theme: 'Urbex / Puzzle-solving', description: 'Has uncovered hidden doors, caches, and locked-away memories.' },
  { tier: 4, name: 'Trail Scribe', badge: '📜', pointsRequired: 600, theme: 'Historical / Story', description: 'Records the lore of routes, POIs, and places long buried in time.' },
  { tier: 5, name: 'Wayfarer', badge: '🧭', pointsRequired: 1000, theme: 'Hiking / Urban Mapping', description: 'Traverses both overgrown rail tracks and rooftop routes. Resourceful and stealthy.' },
  { tier: 6, name: 'Catacomb Courier', badge: '🏛️', pointsRequired: 1500, theme: 'Urbex / High Risk', description: 'Has ventured deep into vaults, tunnels, and off-map passageways.' },
  { tier: 7, name: 'Territory Weaver', badge: '🕸️', pointsRequired: 2200, theme: 'Social / Creative Mapping', description: 'Connects POIs into living chains, creating adventures from fragments of the past.' },
  { tier: 8, name: 'Archivist Ranger', badge: '📚', pointsRequired: 3000, theme: 'History / Guide Role', description: 'Curates and maintains adventure lore, preserving memory for others.' },
  { tier: 9, name: 'Ascent Seeker', badge: '⛰️', pointsRequired: 4000, theme: 'Hiking / Gamification', description: 'Has conquered elevation, speed, and stamina quests. Always climbing.' },
  { tier: 10, name: 'Chrono Cartographer', badge: '🗺️', pointsRequired: 5500, theme: 'Master Rank', description: 'Weaves time, space, and story. Leaves paths that others follow for decades.' },
]

// Unit conversion utilities
const fahrenheitToCelsius = (f: number): number => Math.round((f - 32) * 5 / 9)
const celsiusToFahrenheit = (c: number): number => Math.round(c * 9 / 5 + 32)
const milesToKm = (miles: number): number => Math.round(miles * 1.60934 * 10) / 10
const kmToMiles = (km: number): number => Math.round(km / 1.60934 * 10) / 10
const mphToKmh = (mph: number): number => Math.round(mph * 1.60934)
const kmhToMph = (kmh: number): number => Math.round(kmh / 1.60934)
const inchesToMm = (inches: number): number => Math.round(inches * 25.4 * 10) / 10
const mmToInches = (mm: number): number => Math.round(mm / 25.4 * 100) / 100
const feetToMeters = (feet: number): number => Math.round(feet * 0.3048)
const metersToFeet = (meters: number): number => Math.round(meters / 0.3048)
const feetToMiles = (feet: number): number => feet / 5280

interface ConditionConfigPanelProps {
  conditionType: WorkflowConditionType
  config: Record<string, unknown>
  onChange: (config: Record<string, unknown>) => void
  measurementUnit: MeasurementUnit
  adventureId?: string
  isReadOnly?: boolean
}

interface WeatherCondition {
  code: number
  day: string
  night: string
  icon: number
}

interface GroupOption {
  id: string
  name: string
  description?: string
  memberCount?: number
  groupImage?: string
}

// Wind direction grid positions
const WIND_DIRECTIONS = [
  { key: 'NW', label: 'NW', row: 0, col: 0 },
  { key: 'N', label: 'N', row: 0, col: 1 },
  { key: 'NE', label: 'NE', row: 0, col: 2 },
  { key: 'W', label: 'W', row: 1, col: 0 },
  { key: 'CALM', label: '○', row: 1, col: 1 },
  { key: 'E', label: 'E', row: 1, col: 2 },
  { key: 'SW', label: 'SW', row: 2, col: 0 },
  { key: 'S', label: 'S', row: 2, col: 1 },
  { key: 'SE', label: 'SE', row: 2, col: 2 },
]

export function ConditionConfigPanel({
  conditionType,
  config: configProp,
  onChange,
  measurementUnit,
  adventureId,
  isReadOnly = false,
}: ConditionConfigPanelProps) {
  // Ensure config is never undefined to prevent property access errors
  const config = configProp ?? {}
  const isMetric = measurementUnit === MeasurementUnit.Metric

  // Get cached tags for User Interest and Gender Identity criteria
  const { getCategoriesByType, isLoading: isLoadingTags } = useCachedTags()

  // UI state for tag selection accordions
  const [expandedInterestCategory, setExpandedInterestCategory] = useState<string | null>(null)
  const [expandedGenderCategory, setExpandedGenderCategory] = useState<string | null>(null)

  // Temperature Range - API schema: minTemperature, maxTemperature, temperatureUnit, useUserLocation, fixedLatitude, fixedLongitude
  const renderTemperatureRange = () => {
    const temperatureUnit = (config.temperatureUnit as string) ?? (isMetric ? 'C' : 'F')
    const useUserLocation = (config.useUserLocation as boolean) ?? true
    const fixedLatitude = (config.fixedLatitude as number) ?? 0
    const fixedLongitude = (config.fixedLongitude as number) ?? 0

    // Store in user's preferred unit
    const isConfigMetric = temperatureUnit === 'C'
    const minTemperature = (config.minTemperature as number) ?? (isConfigMetric ? -29 : -20)
    const maxTemperature = (config.maxTemperature as number) ?? (isConfigMetric ? 43 : 110)

    const sliderMin = isConfigMetric ? -29 : -20
    const sliderMax = isConfigMetric ? 43 : 110
    const unit = isConfigMetric ? '°C' : '°F'

    const handleChange = (_event: Event, value: number | number[]) => {
      const [newMin, newMax] = value as number[]
      onChange({ ...config, minTemperature: newMin, maxTemperature: newMax })
    }

    const handleUnitChange = (newUnit: 'F' | 'C') => {
      // Convert existing values to new unit
      const currentUnit = temperatureUnit
      if (currentUnit === newUnit) return

      let newMin: number, newMax: number
      if (newUnit === 'C') {
        newMin = fahrenheitToCelsius(minTemperature)
        newMax = fahrenheitToCelsius(maxTemperature)
      } else {
        newMin = celsiusToFahrenheit(minTemperature)
        newMax = celsiusToFahrenheit(maxTemperature)
      }
      onChange({ ...config, temperatureUnit: newUnit, minTemperature: newMin, maxTemperature: newMax })
    }

    return (
      <Box>
        {/* Unit selector */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip
            label="°F"
            onClick={() => handleUnitChange('F')}
            color={temperatureUnit === 'F' ? 'primary' : 'default'}
            variant={temperatureUnit === 'F' ? 'filled' : 'outlined'}
            size="small"
          />
          <Chip
            label="°C"
            onClick={() => handleUnitChange('C')}
            color={temperatureUnit === 'C' ? 'primary' : 'default'}
            variant={temperatureUnit === 'C' ? 'filled' : 'outlined'}
            size="small"
          />
        </Box>

        <Typography variant="subtitle2" gutterBottom>
          Temperature Range: {minTemperature}{unit} to {maxTemperature}{unit}
        </Typography>
        <Slider
          value={[minTemperature, maxTemperature]}
          onChange={handleChange}
          min={sliderMin}
          max={sliderMax}
          step={1}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}${unit}`}
          marks={[
            { value: sliderMin, label: `${sliderMin}${unit}` },
            { value: isConfigMetric ? 0 : 32, label: isConfigMetric ? '0°C' : '32°F' },
            { value: sliderMax, label: `${sliderMax}${unit}` },
          ]}
        />

        {/* Location source */}
        <LocationSourceSection
          useUserLocation={useUserLocation}
          fixedLatitude={fixedLatitude}
          fixedLongitude={fixedLongitude}
          onUseUserLocationChange={(val) => onChange({ ...config, useUserLocation: val })}
          onLocationChange={(lat, lng) => onChange({ ...config, fixedLatitude: lat, fixedLongitude: lng })}
        />
      </Box>
    )
  }

  // Humidity Range - API schema: minHumidity, maxHumidity (0-100), useUserLocation, fixedLatitude, fixedLongitude
  const renderHumidityRange = () => {
    const minHumidity = (config.minHumidity as number) ?? 0
    const maxHumidity = (config.maxHumidity as number) ?? 100
    const useUserLocation = (config.useUserLocation as boolean) ?? true
    const fixedLatitude = (config.fixedLatitude as number) ?? 0
    const fixedLongitude = (config.fixedLongitude as number) ?? 0

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Humidity Range: {minHumidity}% to {maxHumidity}%
        </Typography>
        <Slider
          value={[minHumidity, maxHumidity]}
          onChange={(_e, v) => {
            const [min, max] = v as number[]
            onChange({ ...config, minHumidity: min, maxHumidity: max })
          }}
          min={0}
          max={100}
          step={5}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}%`}
          marks={[
            { value: 0, label: '0%' },
            { value: 50, label: '50%' },
            { value: 100, label: '100%' },
          ]}
        />

        {/* Location source */}
        <LocationSourceSection
          useUserLocation={useUserLocation}
          fixedLatitude={fixedLatitude}
          fixedLongitude={fixedLongitude}
          onUseUserLocationChange={(val) => onChange({ ...config, useUserLocation: val })}
          onLocationChange={(lat, lng) => onChange({ ...config, fixedLatitude: lat, fixedLongitude: lng })}
        />
      </Box>
    )
  }

  // Day/Night toggle - API schema: requiredTimeOfDay ("day" or "night"), useUserLocation, fixedLatitude, fixedLongitude
  const renderDaytime = () => {
    const requiredTimeOfDay = (config.requiredTimeOfDay as string) ?? 'day'
    const useUserLocation = (config.useUserLocation as boolean) ?? true
    const fixedLatitude = (config.fixedLatitude as number) ?? 0
    const fixedLongitude = (config.fixedLongitude as number) ?? 0

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Required Time of Day
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip
            label="☀️ Day"
            onClick={() => onChange({ ...config, requiredTimeOfDay: 'day' })}
            color={requiredTimeOfDay === 'day' ? 'primary' : 'default'}
            variant={requiredTimeOfDay === 'day' ? 'filled' : 'outlined'}
          />
          <Chip
            label="🌙 Night"
            onClick={() => onChange({ ...config, requiredTimeOfDay: 'night' })}
            color={requiredTimeOfDay === 'night' ? 'primary' : 'default'}
            variant={requiredTimeOfDay === 'night' ? 'filled' : 'outlined'}
          />
        </Box>

        {/* Location source */}
        <LocationSourceSection
          useUserLocation={useUserLocation}
          fixedLatitude={fixedLatitude}
          fixedLongitude={fixedLongitude}
          onUseUserLocationChange={(val) => onChange({ ...config, useUserLocation: val })}
          onLocationChange={(lat, lng) => onChange({ ...config, fixedLatitude: lat, fixedLongitude: lng })}
        />
      </Box>
    )
  }

  // Wind Speed Range - API schema: minWindSpeed, maxWindSpeed (stored in mph), useUserLocation, fixedLatitude, fixedLongitude
  // Display in user's preferred unit (km/h for metric, mph for imperial), but always store in mph
  const renderWindSpeed = () => {
    const useUserLocation = (config.useUserLocation as boolean) ?? true
    const fixedLatitude = (config.fixedLatitude as number) ?? 0
    const fixedLongitude = (config.fixedLongitude as number) ?? 0

    // Values stored in mph
    const minWindSpeedMph = (config.minWindSpeed as number) ?? 0
    const maxWindSpeedMph = (config.maxWindSpeed as number) ?? 75

    // Display values converted to user's preferred unit
    const displayMin = isMetric ? mphToKmh(minWindSpeedMph) : minWindSpeedMph
    const displayMax = isMetric ? mphToKmh(maxWindSpeedMph) : maxWindSpeedMph
    const unit = isMetric ? 'km/h' : 'mph'

    // Slider range in display units
    const sliderMin = 0
    const sliderMax = isMetric ? mphToKmh(75) : 75 // 75 mph = ~121 km/h

    const handleChange = (_event: Event, value: number | number[]) => {
      const [newMin, newMax] = value as number[]
      // Convert back to mph for storage
      const storedMin = isMetric ? kmhToMph(newMin) : newMin
      const storedMax = isMetric ? kmhToMph(newMax) : newMax
      onChange({ ...config, minWindSpeed: storedMin, maxWindSpeed: storedMax })
    }

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Wind Speed Range: {displayMin} {unit} to {displayMax} {unit}
        </Typography>
        <Slider
          value={[displayMin, displayMax]}
          onChange={handleChange}
          min={sliderMin}
          max={sliderMax}
          step={isMetric ? 5 : 5}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v} ${unit}`}
          marks={[
            { value: sliderMin, label: `0` },
            { value: Math.round(sliderMax / 2), label: `${Math.round(sliderMax / 2)}` },
            { value: sliderMax, label: `${sliderMax}` },
          ]}
        />

        {/* Location source */}
        <LocationSourceSection
          useUserLocation={useUserLocation}
          fixedLatitude={fixedLatitude}
          fixedLongitude={fixedLongitude}
          onUseUserLocationChange={(val) => onChange({ ...config, useUserLocation: val })}
          onLocationChange={(lat, lng) => onChange({ ...config, fixedLatitude: lat, fixedLongitude: lng })}
        />
      </Box>
    )
  }

  // Wind Direction grid - API schema: allowedDirections (comma-separated: "N,NE,E,SE,S,SW,W,NW"), minDirection, maxDirection (0-360), useUserLocation, fixedLatitude, fixedLongitude
  const renderWindDirection = () => {
    // Parse allowedDirections from comma-separated string or use array
    const allowedDirectionsRaw = config.allowedDirections
    const directions: string[] = typeof allowedDirectionsRaw === 'string'
      ? (allowedDirectionsRaw as string).split(',').filter(d => d.trim())
      : (allowedDirectionsRaw as string[]) ?? []

    const useUserLocation = (config.useUserLocation as boolean) ?? true
    const fixedLatitude = (config.fixedLatitude as number) ?? 0
    const fixedLongitude = (config.fixedLongitude as number) ?? 0
    const minWindSpeed = (config.minWindSpeed as number) ?? 0
    const isCalmDisabled = minWindSpeed > 0

    const toggleDirection = (dir: string) => {
      let newDirs: string[]
      if (directions.includes(dir)) {
        newDirs = directions.filter((d) => d !== dir)
      } else {
        newDirs = [...directions, dir]
      }
      // Store as comma-separated string for API compatibility
      onChange({ ...config, allowedDirections: newDirs.join(',') })
    }

    // If calm is disabled but was previously selected, remove it
    if (isCalmDisabled && directions.includes('CALM')) {
      const newDirs = directions.filter((d) => d !== 'CALM')
      onChange({ ...config, allowedDirections: newDirs.join(',') })
    }

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Wind Direction (select valid directions)
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 0.5,
            maxWidth: 180,
            mx: 'auto',
            mt: 1,
          }}
        >
          {WIND_DIRECTIONS.map((dir) => {
            const isDisabled = dir.key === 'CALM' && isCalmDisabled
            return (
              <ToggleButton
                key={dir.key}
                value={dir.key}
                selected={directions.includes(dir.key)}
                onChange={() => toggleDirection(dir.key)}
                disabled={isDisabled}
                size="small"
                sx={{
                  width: 50,
                  height: 50,
                  fontSize: dir.key === 'CALM' ? '1.2rem' : '0.75rem',
                  fontWeight: 'bold',
                  ...(isDisabled && {
                    opacity: 0.4,
                    cursor: 'not-allowed',
                  }),
                }}
              >
                {dir.label}
              </ToggleButton>
            )
          })}
        </Box>
        {isCalmDisabled && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center', fontStyle: 'italic' }}>
            Calm disabled (min wind speed &gt; 0)
          </Typography>
        )}
        {directions.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
            Selected: {directions.join(', ')}
          </Typography>
        )}

        {/* Location source */}
        <LocationSourceSection
          useUserLocation={useUserLocation}
          fixedLatitude={fixedLatitude}
          fixedLongitude={fixedLongitude}
          onUseUserLocationChange={(val) => onChange({ ...config, useUserLocation: val })}
          onLocationChange={(lat, lng) => onChange({ ...config, fixedLatitude: lat, fixedLongitude: lng })}
        />
      </Box>
    )
  }

  // Cloud Cover Range - API schema: minCloudCover, maxCloudCover (0-100), useUserLocation, fixedLatitude, fixedLongitude
  const renderCloudCover = () => {
    const minCloudCover = (config.minCloudCover as number) ?? 0
    const maxCloudCover = (config.maxCloudCover as number) ?? 100
    const useUserLocation = (config.useUserLocation as boolean) ?? true
    const fixedLatitude = (config.fixedLatitude as number) ?? 0
    const fixedLongitude = (config.fixedLongitude as number) ?? 0

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Cloud Cover Range: {minCloudCover}% to {maxCloudCover}%
        </Typography>
        <Slider
          value={[minCloudCover, maxCloudCover]}
          onChange={(_e, v) => {
            const [min, max] = v as number[]
            onChange({ ...config, minCloudCover: min, maxCloudCover: max })
          }}
          min={0}
          max={100}
          step={5}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}%`}
          marks={[
            { value: 0, label: 'Clear' },
            { value: 50, label: '50%' },
            { value: 100, label: 'Overcast' },
          ]}
        />

        {/* Location source */}
        <LocationSourceSection
          useUserLocation={useUserLocation}
          fixedLatitude={fixedLatitude}
          fixedLongitude={fixedLongitude}
          onUseUserLocationChange={(val) => onChange({ ...config, useUserLocation: val })}
          onLocationChange={(lat, lng) => onChange({ ...config, fixedLatitude: lat, fixedLongitude: lng })}
        />
      </Box>
    )
  }

  // Visibility Range - API schema: minVisibility, maxVisibility (stored in miles), useUserLocation, fixedLatitude, fixedLongitude
  // Display in user's preferred unit (km for metric, miles for imperial), but always store in miles
  const renderVisibility = () => {
    const useUserLocation = (config.useUserLocation as boolean) ?? true
    const fixedLatitude = (config.fixedLatitude as number) ?? 0
    const fixedLongitude = (config.fixedLongitude as number) ?? 0

    // Values stored in miles
    const minVisibilityMiles = (config.minVisibility as number) ?? 0
    const maxVisibilityMiles = (config.maxVisibility as number) ?? 10

    // Display values converted to user's preferred unit
    const displayMin = isMetric ? milesToKm(minVisibilityMiles) : minVisibilityMiles
    const displayMax = isMetric ? milesToKm(maxVisibilityMiles) : maxVisibilityMiles
    const unit = isMetric ? 'km' : 'mi'

    // Slider range in display units
    const sliderMin = 0
    const sliderMax = isMetric ? milesToKm(10) : 10 // 10 miles = ~16 km

    const handleChange = (_event: Event, value: number | number[]) => {
      const [newMin, newMax] = value as number[]
      // Convert back to miles for storage
      const storedMin = isMetric ? kmToMiles(newMin) : newMin
      const storedMax = isMetric ? kmToMiles(newMax) : newMax
      onChange({ ...config, minVisibility: storedMin, maxVisibility: storedMax })
    }

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Visibility Range: {displayMin} {unit} to {displayMax} {unit}
        </Typography>
        <Slider
          value={[displayMin, displayMax]}
          onChange={handleChange}
          min={sliderMin}
          max={sliderMax}
          step={isMetric ? 0.5 : 0.5}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v} ${unit}`}
          marks={[
            { value: sliderMin, label: '0' },
            { value: sliderMax, label: `${sliderMax}` },
          ]}
        />

        {/* Location source */}
        <LocationSourceSection
          useUserLocation={useUserLocation}
          fixedLatitude={fixedLatitude}
          fixedLongitude={fixedLongitude}
          onUseUserLocationChange={(val) => onChange({ ...config, useUserLocation: val })}
          onLocationChange={(lat, lng) => onChange({ ...config, fixedLatitude: lat, fixedLongitude: lng })}
        />
      </Box>
    )
  }

  // Weather Condition multi-select - API schema: allowedConditions (comma-separated: "clear,sunny,partly_cloudy,cloudy,rain,snow,fog,thunderstorm"), excludedConditions, useUserLocation, fixedLatitude, fixedLongitude
  // Map weather condition names to codes for internal use
  const WEATHER_CONDITION_MAP: Record<string, string> = {
    'clear': 'clear',
    'sunny': 'sunny',
    'partly_cloudy': 'partly_cloudy',
    'cloudy': 'cloudy',
    'rain': 'rain',
    'snow': 'snow',
    'fog': 'fog',
    'thunderstorm': 'thunderstorm',
    'drizzle': 'drizzle',
    'sleet': 'sleet',
    'hail': 'hail',
    'mist': 'mist',
    'overcast': 'overcast',
  }

  const WEATHER_CONDITION_OPTIONS = [
    { key: 'clear', label: 'Clear', icon: '☀️' },
    { key: 'sunny', label: 'Sunny', icon: '🌞' },
    { key: 'partly_cloudy', label: 'Partly Cloudy', icon: '⛅' },
    { key: 'cloudy', label: 'Cloudy', icon: '☁️' },
    { key: 'overcast', label: 'Overcast', icon: '🌥️' },
    { key: 'rain', label: 'Rain', icon: '🌧️' },
    { key: 'drizzle', label: 'Drizzle', icon: '🌦️' },
    { key: 'thunderstorm', label: 'Thunderstorm', icon: '⛈️' },
    { key: 'snow', label: 'Snow', icon: '❄️' },
    { key: 'sleet', label: 'Sleet', icon: '🌨️' },
    { key: 'hail', label: 'Hail', icon: '🧊' },
    { key: 'fog', label: 'Fog', icon: '🌫️' },
    { key: 'mist', label: 'Mist', icon: '🌁' },
  ]

  const renderWeatherCondition = () => {
    // Parse allowedConditions from comma-separated string
    const allowedConditionsRaw = config.allowedConditions
    const allowedConditions: string[] = typeof allowedConditionsRaw === 'string'
      ? (allowedConditionsRaw as string).split(',').filter(c => c.trim())
      : (allowedConditionsRaw as string[]) ?? []

    const excludedConditionsRaw = config.excludedConditions
    const excludedConditions: string[] = typeof excludedConditionsRaw === 'string'
      ? (excludedConditionsRaw as string).split(',').filter(c => c.trim())
      : (excludedConditionsRaw as string[]) ?? []

    const useUserLocation = (config.useUserLocation as boolean) ?? true
    const fixedLatitude = (config.fixedLatitude as number) ?? 0
    const fixedLongitude = (config.fixedLongitude as number) ?? 0

    const toggleCondition = (conditionKey: string, listType: 'allowed' | 'excluded') => {
      if (listType === 'allowed') {
        let newAllowed: string[]
        if (allowedConditions.includes(conditionKey)) {
          newAllowed = allowedConditions.filter(c => c !== conditionKey)
        } else {
          newAllowed = [...allowedConditions, conditionKey]
          // Remove from excluded if adding to allowed
          if (excludedConditions.includes(conditionKey)) {
            const newExcluded = excludedConditions.filter(c => c !== conditionKey)
            onChange({ ...config, allowedConditions: newAllowed.join(','), excludedConditions: newExcluded.join(',') })
            return
          }
        }
        onChange({ ...config, allowedConditions: newAllowed.join(',') })
      } else {
        let newExcluded: string[]
        if (excludedConditions.includes(conditionKey)) {
          newExcluded = excludedConditions.filter(c => c !== conditionKey)
        } else {
          newExcluded = [...excludedConditions, conditionKey]
          // Remove from allowed if adding to excluded
          if (allowedConditions.includes(conditionKey)) {
            const newAllowed = allowedConditions.filter(c => c !== conditionKey)
            onChange({ ...config, allowedConditions: newAllowed.join(','), excludedConditions: newExcluded.join(',') })
            return
          }
        }
        onChange({ ...config, excludedConditions: newExcluded.join(',') })
      }
    }

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Weather Conditions
        </Typography>

        {/* Allowed conditions */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
          Allowed Conditions (must match one):
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {WEATHER_CONDITION_OPTIONS.map((condition) => (
            <Chip
              key={condition.key}
              label={`${condition.icon} ${condition.label}`}
              onClick={() => toggleCondition(condition.key, 'allowed')}
              color={allowedConditions.includes(condition.key) ? 'primary' : 'default'}
              variant={allowedConditions.includes(condition.key) ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Box>

        {/* Excluded conditions - collapsible */}
        <Accordion sx={{ mt: 1, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">
              Excluded Conditions ({excludedConditions.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {WEATHER_CONDITION_OPTIONS.map((condition) => (
                <Chip
                  key={condition.key}
                  label={`${condition.icon} ${condition.label}`}
                  onClick={() => toggleCondition(condition.key, 'excluded')}
                  color={excludedConditions.includes(condition.key) ? 'error' : 'default'}
                  variant={excludedConditions.includes(condition.key) ? 'filled' : 'outlined'}
                  size="small"
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Location source */}
        <LocationSourceSection
          useUserLocation={useUserLocation}
          fixedLatitude={fixedLatitude}
          fixedLongitude={fixedLongitude}
          onUseUserLocationChange={(val) => onChange({ ...config, useUserLocation: val })}
          onLocationChange={(lat, lng) => onChange({ ...config, fixedLatitude: lat, fixedLongitude: lng })}
        />
      </Box>
    )
  }

  // Sunset/Sunrise - API schema: eventType ("sunrise" or "sunset"), timing ("before", "after", "during"), minutesBefore, minutesAfter, durationMinutes, useUserLocation, fixedLatitude, fixedLongitude
  const renderSunsetSunrise = () => {
    const eventType = (config.eventType as 'sunrise' | 'sunset') ?? 'sunrise'
    const timing = (config.timing as 'before' | 'after' | 'during') ?? 'during'
    const minutesBefore = (config.minutesBefore as number) ?? 30
    const minutesAfter = (config.minutesAfter as number) ?? 30
    const durationMinutes = (config.durationMinutes as number) ?? 30
    const useUserLocation = (config.useUserLocation as boolean) ?? true
    const fixedLatitude = (config.fixedLatitude as number) ?? 0
    const fixedLongitude = (config.fixedLongitude as number) ?? 0

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Sun Event
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip
            label="🌅 Sunrise"
            onClick={() => onChange({ ...config, eventType: 'sunrise' })}
            color={eventType === 'sunrise' ? 'primary' : 'default'}
            variant={eventType === 'sunrise' ? 'filled' : 'outlined'}
          />
          <Chip
            label="🌇 Sunset"
            onClick={() => onChange({ ...config, eventType: 'sunset' })}
            color={eventType === 'sunset' ? 'primary' : 'default'}
            variant={eventType === 'sunset' ? 'filled' : 'outlined'}
          />
        </Box>

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Timing
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip
            label="Before"
            onClick={() => onChange({ ...config, timing: 'before' })}
            color={timing === 'before' ? 'primary' : 'default'}
            variant={timing === 'before' ? 'filled' : 'outlined'}
            size="small"
          />
          <Chip
            label="During"
            onClick={() => onChange({ ...config, timing: 'during' })}
            color={timing === 'during' ? 'primary' : 'default'}
            variant={timing === 'during' ? 'filled' : 'outlined'}
            size="small"
          />
          <Chip
            label="After"
            onClick={() => onChange({ ...config, timing: 'after' })}
            color={timing === 'after' ? 'primary' : 'default'}
            variant={timing === 'after' ? 'filled' : 'outlined'}
            size="small"
          />
        </Box>

        {timing === 'before' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Minutes before {eventType}: {minutesBefore}
            </Typography>
            <Slider
              value={minutesBefore}
              onChange={(_e, v) => onChange({ ...config, minutesBefore: v as number })}
              min={5}
              max={120}
              step={5}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v} min`}
              marks={[
                { value: 5, label: '5' },
                { value: 30, label: '30' },
                { value: 60, label: '60' },
                { value: 120, label: '120' },
              ]}
            />
          </Box>
        )}

        {timing === 'after' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Minutes after {eventType}: {minutesAfter}
            </Typography>
            <Slider
              value={minutesAfter}
              onChange={(_e, v) => onChange({ ...config, minutesAfter: v as number })}
              min={5}
              max={120}
              step={5}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v} min`}
              marks={[
                { value: 5, label: '5' },
                { value: 30, label: '30' },
                { value: 60, label: '60' },
                { value: 120, label: '120' },
              ]}
            />
          </Box>
        )}

        {timing === 'during' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Duration window: {durationMinutes} minutes around {eventType}
            </Typography>
            <Slider
              value={durationMinutes}
              onChange={(_e, v) => onChange({ ...config, durationMinutes: v as number })}
              min={10}
              max={60}
              step={5}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `±${v / 2} min`}
              marks={[
                { value: 10, label: '±5' },
                { value: 30, label: '±15' },
                { value: 60, label: '±30' },
              ]}
            />
          </Box>
        )}

        {/* Location source */}
        <LocationSourceSection
          useUserLocation={useUserLocation}
          fixedLatitude={fixedLatitude}
          fixedLongitude={fixedLongitude}
          onUseUserLocationChange={(val) => onChange({ ...config, useUserLocation: val })}
          onLocationChange={(lat, lng) => onChange({ ...config, fixedLatitude: lat, fixedLongitude: lng })}
        />
      </Box>
    )
  }

  // Precipitation per hour (stored in inches)
  const renderPrecipitation = () => {
    const minPrecip = (config.minPrecipitation as number) ?? 0
    const maxPrecip = (config.maxPrecipitation as number) ?? 4

    const displayMin = isMetric ? inchesToMm(minPrecip) : minPrecip
    const displayMax = isMetric ? inchesToMm(maxPrecip) : maxPrecip
    const unit = isMetric ? 'mm' : 'in'

    const sliderMin = 0
    const sliderMax = isMetric ? inchesToMm(4) : 4

    const handleChange = (_event: Event, value: number | number[]) => {
      const [newMin, newMax] = value as number[]
      const storedMin = isMetric ? mmToInches(newMin) : newMin
      const storedMax = isMetric ? mmToInches(newMax) : newMax
      onChange({ ...config, minPrecipitation: storedMin, maxPrecipitation: storedMax })
    }

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Precipitation per Hour: {displayMin} {unit} to {displayMax} {unit}
        </Typography>
        <Slider
          value={[displayMin, displayMax]}
          onChange={handleChange}
          min={sliderMin}
          max={sliderMax}
          step={isMetric ? 5 : 0.25}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v} ${unit}`}
          marks={[
            { value: sliderMin, label: '0' },
            { value: sliderMax, label: `${sliderMax}` },
          ]}
        />
      </Box>
    )
  }

  // UV Index Range (0-11)
  const renderUvIndex = () => {
    const minUv = (config.minUvIndex as number) ?? 0
    const maxUv = (config.maxUvIndex as number) ?? 11

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          UV Index Range: {minUv} to {maxUv}
        </Typography>
        <Slider
          value={[minUv, maxUv]}
          onChange={(_e, v) => {
            const [min, max] = v as number[]
            onChange({ ...config, minUvIndex: min, maxUvIndex: max })
          }}
          min={0}
          max={11}
          step={1}
          valueLabelDisplay="auto"
          marks={[
            { value: 0, label: '0' },
            { value: 3, label: 'Low' },
            { value: 6, label: 'High' },
            { value: 8, label: 'Very High' },
            { value: 11, label: 'Extreme' },
          ]}
        />
      </Box>
    )
  }

  // Group Membership multi-select with search
  const [groupSearchInput, setGroupSearchInput] = useState('')
  const [debouncedGroupSearch, setDebouncedGroupSearch] = useState('')

  // Debounce the search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedGroupSearch(groupSearchInput)
    }, 300)
    return () => clearTimeout(timer)
  }, [groupSearchInput])

  // Query for group search - must be at top level (not inside render function)
  const { data: groupSearchResults = [], isLoading: isSearchingGroups } = useQuery({
    queryKey: ['group-search', debouncedGroupSearch],
    queryFn: async () => {
      if (!debouncedGroupSearch || debouncedGroupSearch.length < 2) {
        return []
      }
      try {
        const response = await ApiService.get<GroupOption[]>(
          `/api/groups/search?query=${encodeURIComponent(debouncedGroupSearch)}`
        )
        return response
      } catch {
        return []
      }
    },
    enabled: debouncedGroupSearch.length >= 2,
  })

  // Collection search state
  const [collectionSearchInput, setCollectionSearchInput] = useState('')
  const [debouncedCollectionSearch, setDebouncedCollectionSearch] = useState('')

  // Debounce collection search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCollectionSearch(collectionSearchInput)
    }, 300)
    return () => clearTimeout(timer)
  }, [collectionSearchInput])

  // Query for collection search
  const { data: collectionSearchResults = [], isLoading: isSearchingCollections } = useQuery({
    queryKey: ['collection-search', debouncedCollectionSearch],
    queryFn: async () => {
      if (!debouncedCollectionSearch || debouncedCollectionSearch.length < 2) {
        return []
      }
      try {
        const response = await searchApi.search({
          query: debouncedCollectionSearch,
          type: SearchEntityType.Collections,
        })
        return response.collections ?? []
      } catch {
        return []
      }
    },
    enabled: debouncedCollectionSearch.length >= 2,
  })

  // Adventure search state
  const [adventureSearchInput, setAdventureSearchInput] = useState('')
  const [debouncedAdventureSearch, setDebouncedAdventureSearch] = useState('')

  // Debounce adventure search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAdventureSearch(adventureSearchInput)
    }, 300)
    return () => clearTimeout(timer)
  }, [adventureSearchInput])

  // Query for adventure search
  const { data: adventureSearchResults = [], isLoading: isSearchingAdventures } = useQuery({
    queryKey: ['adventure-search', debouncedAdventureSearch],
    queryFn: async () => {
      if (!debouncedAdventureSearch || debouncedAdventureSearch.length < 2) {
        return []
      }
      try {
        const response = await searchApi.search({
          query: debouncedAdventureSearch,
          type: SearchEntityType.Adventure,
        })
        return response.adventures ?? []
      } catch {
        return []
      }
    },
    enabled: debouncedAdventureSearch.length >= 2,
  })

  // Group Membership - API schema: requiredGroups (array of group objects with id/name), requiredGroupId (single GUID), requiresAllGroups (boolean, default false = OR logic)
  const renderGroupMembership = () => {
    // requiredGroups from API contains full objects with id, name, memberCount
    // We use this directly for display and storage
    const requiredGroupsRaw = config.requiredGroups
    const selectedGroups: GroupOption[] = Array.isArray(requiredGroupsRaw)
      ? (requiredGroupsRaw as Array<{ id: string; name: string; memberCount?: number }>).map((g) => ({
          id: g.id,
          name: g.name,
          memberCount: g.memberCount,
        }))
      : []
    // API property: requiresAllGroups - if true, user must be member of ALL groups; if false (default), user must be member of ANY
    const requiresAllGroups = (config.requiresAllGroups as boolean) ?? false

    // Filter out already-selected groups from search results
    const filteredResults = groupSearchResults.filter(
      (sr) => !selectedGroups.some((sg) => sg.id === sr.id)
    )

    const handleSelectGroup = (group: GroupOption) => {
      const newSelected = [...selectedGroups, { id: group.id, name: group.name, memberCount: group.memberCount }]
      onChange({
        ...config,
        // API uses requiredGroups as array of group objects
        requiredGroups: newSelected,
        // Also set requiredGroupId to first group for backward compatibility
        requiredGroupId: newSelected.length > 0 ? newSelected[0].id : undefined,
      })
      setGroupSearchInput('') // Clear search after selection
    }

    const handleRemoveGroup = (groupId: string) => {
      const newSelected = selectedGroups.filter((g) => g.id !== groupId)
      onChange({
        ...config,
        requiredGroups: newSelected,
        requiredGroupId: newSelected.length > 0 ? newSelected[0].id : undefined,
      })
    }

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Group Membership
        </Typography>

        {/* Match mode - requiresAllGroups */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip
            label="Any Group (OR)"
            onClick={() => onChange({ ...config, requiresAllGroups: false })}
            color={!requiresAllGroups ? 'primary' : 'default'}
            variant={!requiresAllGroups ? 'filled' : 'outlined'}
            size="small"
          />
          <Chip
            label="All Groups (AND)"
            onClick={() => onChange({ ...config, requiresAllGroups: true })}
            color={requiresAllGroups ? 'primary' : 'default'}
            variant={requiresAllGroups ? 'filled' : 'outlined'}
            size="small"
          />
        </Box>

        {/* Selected groups as chips */}
        {selectedGroups.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
            {selectedGroups.map((group) => (
              <Chip
                key={group.id}
                label={group.name}
                size="small"
                onDelete={() => handleRemoveGroup(group.id)}
              />
            ))}
          </Box>
        )}

        {/* Search input */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search for groups..."
          value={groupSearchInput}
          onChange={(e) => setGroupSearchInput(e.target.value)}
          helperText={
            groupSearchInput.length > 0 && groupSearchInput.length < 2
              ? "Type at least 2 characters to search"
              : undefined
          }
        />

        {/* Search results */}
        {groupSearchInput.length >= 2 && (
          <Paper
            variant="outlined"
            sx={{
              mt: 1,
              maxHeight: 200,
              overflow: 'auto',
            }}
          >
            {isSearchingGroups ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Searching...
                </Typography>
              </Box>
            ) : filteredResults.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No groups found
                </Typography>
              </Box>
            ) : (
              filteredResults.map((group) => (
                <Box
                  key={group.id}
                  onClick={() => handleSelectGroup(group)}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    borderBottom: '1px solid',
                    borderBottomColor: 'divider',
                    '&:last-child': { borderBottom: 'none' },
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Typography variant="body2">{group.name}</Typography>
                  {group.memberCount !== undefined && (
                    <Typography variant="caption" color="text.secondary">
                      {group.memberCount} member{group.memberCount !== 1 ? 's' : ''}
                    </Typography>
                  )}
                </Box>
              ))
            )}
          </Paper>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          User must be a member of {requiresAllGroups ? 'all' : 'at least one'} selected group{selectedGroups.length !== 1 ? 's' : ''}
        </Typography>
      </Box>
    )
  }

  // Adventure Collection % Completed - API schema: collectionId (GUID), requiredCompletionPercentage (0-100), liveUpdating (boolean)
  const renderCollectionCompletion = () => {
    // Store selected collection as object with id and name for display
    const selectedCollection = (config.selectedCollection as CollectionSearchResult | null) ?? null
    const requiredCompletionPercentage = (config.requiredCompletionPercentage as number) ?? 100
    const liveUpdating = (config.liveUpdating as boolean) ?? true

    const handleSelectCollection = (collection: CollectionSearchResult) => {
      onChange({
        ...config,
        collectionId: collection.id,
        selectedCollection: collection,
      })
      setCollectionSearchInput('') // Clear search after selection
    }

    const handleRemoveCollection = () => {
      onChange({
        ...config,
        collectionId: undefined,
        selectedCollection: null,
      })
    }

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Adventure Collection Completion
        </Typography>

        {/* Selected collection as chip */}
        {selectedCollection && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
            <Chip
              label={selectedCollection.name}
              size="small"
              onDelete={handleRemoveCollection}
            />
          </Box>
        )}

        {/* Search input - only show if no collection selected */}
        {!selectedCollection && (
          <>
            <TextField
              fullWidth
              size="small"
              placeholder="Search for collections..."
              value={collectionSearchInput}
              onChange={(e) => setCollectionSearchInput(e.target.value)}
              helperText={
                collectionSearchInput.length > 0 && collectionSearchInput.length < 2
                  ? "Type at least 2 characters to search"
                  : undefined
              }
            />

            {/* Search results */}
            {collectionSearchInput.length >= 2 && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 1,
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                {isSearchingCollections ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Searching...
                    </Typography>
                  </Box>
                ) : collectionSearchResults.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No collections found
                    </Typography>
                  </Box>
                ) : (
                  collectionSearchResults.map((collection) => (
                    <Box
                      key={collection.id}
                      onClick={() => handleSelectCollection(collection)}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        borderBottom: '1px solid',
                        borderBottomColor: 'divider',
                        '&:last-child': { borderBottom: 'none' },
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Typography variant="body2">{collection.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {collection.totalAdventureCount} adventure{collection.totalAdventureCount !== 1 ? 's' : ''} · by {collection.creatorName}
                      </Typography>
                    </Box>
                  ))
                )}
              </Paper>
            )}
          </>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Required Completion: {requiredCompletionPercentage}%
          </Typography>
          <Slider
            value={requiredCompletionPercentage}
            onChange={(_e, v) => {
              onChange({ ...config, requiredCompletionPercentage: v as number })
            }}
            min={0}
            max={100}
            step={5}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
            marks={[
              { value: 0, label: '0%' },
              { value: 50, label: '50%' },
              { value: 100, label: '100%' },
            ]}
            disabled={!selectedCollection}
          />
        </Box>

        {/* Live updating toggle */}
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={liveUpdating}
                onChange={(e) => onChange({ ...config, liveUpdating: e.target.checked })}
              />
            }
            label="Live updating (re-evaluate when collection changes)"
          />
        </Box>
      </Box>
    )
  }

  // Adventure Completed selector - API schema: requiredAdventureId (GUID)
  const renderAdventureCompleted = () => {
    // Store selected adventure as object with id and name for display
    const selectedAdventure = (config.selectedAdventure as AdventureSearchResult | null) ?? null

    const handleSelectAdventure = (adventure: AdventureSearchResult) => {
      onChange({
        ...config,
        // API uses requiredAdventureId
        requiredAdventureId: adventure.id,
        selectedAdventure: adventure,
      })
      setAdventureSearchInput('') // Clear search after selection
    }

    const handleRemoveAdventure = () => {
      onChange({
        ...config,
        requiredAdventureId: undefined,
        selectedAdventure: null,
      })
    }

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Required Adventure Completion
        </Typography>

        {/* Selected adventure as chip */}
        {selectedAdventure && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
            <Chip
              label={selectedAdventure.name || selectedAdventure.title}
              size="small"
              onDelete={handleRemoveAdventure}
            />
          </Box>
        )}

        {/* Search input - only show if no adventure selected */}
        {!selectedAdventure && (
          <>
            <TextField
              fullWidth
              size="small"
              placeholder="Search for adventures..."
              value={adventureSearchInput}
              onChange={(e) => setAdventureSearchInput(e.target.value)}
              helperText={
                adventureSearchInput.length > 0 && adventureSearchInput.length < 2
                  ? "Type at least 2 characters to search"
                  : undefined
              }
            />

            {/* Search results */}
            {adventureSearchInput.length >= 2 && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 1,
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                {isSearchingAdventures ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Searching...
                    </Typography>
                  </Box>
                ) : adventureSearchResults.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No adventures found
                    </Typography>
                  </Box>
                ) : (
                  adventureSearchResults.map((adventure) => (
                    <Box
                      key={adventure.id}
                      onClick={() => handleSelectAdventure(adventure)}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        borderBottom: '1px solid',
                        borderBottomColor: 'divider',
                        '&:last-child': { borderBottom: 'none' },
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Typography variant="body2">{adventure.name || adventure.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        by {adventure.creatorName}
                      </Typography>
                    </Box>
                  ))
                )}
              </Paper>
            )}
          </>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          User must have completed this adventure to proceed
        </Typography>
      </Box>
    )
  }

  // Geofence Location - API schema: centerLatitude, centerLongitude, radiusFeet (10-5280), radiusUnit ("feet", "meters", "miles"), durationMinutes (0-30)
  // Also supports polygon geofence: geofenceType ('circle' | 'polygon'), polygonCoordinates ([lng, lat][] GeoJSON format)
  const [geofenceDialogOpen, setGeofenceDialogOpen] = useState(false)
  const [tempGeofence, setTempGeofence] = useState<{
    geofenceType: 'circle' | 'polygon'
    centerLatitude: number
    centerLongitude: number
    radiusFeet: number
    durationMinutes: number
    polygonCoordinates: [number, number][]
  }>({
    geofenceType: (config.geofenceType as 'circle' | 'polygon') ?? 'circle',
    centerLatitude: (config.centerLatitude as number) ?? 0,
    centerLongitude: (config.centerLongitude as number) ?? 0,
    radiusFeet: (config.radiusFeet as number) ?? 500,
    durationMinutes: (config.durationMinutes as number) ?? 0,
    polygonCoordinates: (config.polygonCoordinates as [number, number][]) ?? [],
  })

  useEffect(() => {
    setTempGeofence({
      geofenceType: (config.geofenceType as 'circle' | 'polygon') ?? 'circle',
      centerLatitude: (config.centerLatitude as number) ?? 0,
      centerLongitude: (config.centerLongitude as number) ?? 0,
      radiusFeet: (config.radiusFeet as number) ?? 500,
      durationMinutes: (config.durationMinutes as number) ?? 0,
      polygonCoordinates: (config.polygonCoordinates as [number, number][]) ?? [],
    })
  }, [config.geofenceType, config.centerLatitude, config.centerLongitude, config.radiusFeet, config.durationMinutes, config.polygonCoordinates])

  const renderGeofence = () => {
    const geofenceType = (config.geofenceType as 'circle' | 'polygon') ?? 'circle'
    const centerLatitude = (config.centerLatitude as number) ?? 0
    const centerLongitude = (config.centerLongitude as number) ?? 0
    const radiusFeet = (config.radiusFeet as number) ?? 500
    const durationMinutes = (config.durationMinutes as number) ?? 0
    const polygonCoordinates = (config.polygonCoordinates as [number, number][]) ?? []

    const displayRadius = isMetric ? feetToMeters(radiusFeet) : radiusFeet
    const unit = isMetric ? 'm' : 'ft'

    const hasCircleLocation = centerLatitude !== 0 || centerLongitude !== 0
    const hasPolygon = polygonCoordinates.length >= 3
    const hasGeofence = geofenceType === 'circle' ? hasCircleLocation : hasPolygon

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Geofence Location
        </Typography>

        {hasGeofence ? (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Type:</strong> {geofenceType === 'circle' ? 'Circle' : 'Polygon'}
            </Typography>
            {geofenceType === 'circle' ? (
              <>
                <Typography variant="body2">
                  <strong>Center:</strong> {centerLatitude.toFixed(6)}, {centerLongitude.toFixed(6)}
                </Typography>
                <Typography variant="body2">
                  <strong>Radius:</strong> {displayRadius} {unit}
                </Typography>
              </>
            ) : (
              <Typography variant="body2">
                <strong>Points:</strong> {polygonCoordinates.length} vertices
              </Typography>
            )}
            {durationMinutes > 0 && (
              <Typography variant="body2">
                <strong>Duration:</strong> {durationMinutes} minute{durationMinutes !== 1 ? 's' : ''}
              </Typography>
            )}
          </Paper>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
            No geofence set
          </Typography>
        )}

        <Button
          variant="outlined"
          onClick={() => setGeofenceDialogOpen(true)}
          fullWidth
        >
          {hasGeofence ? 'Edit Geofence' : 'Set Geofence'}
        </Button>

        <GeofenceDialog
          open={geofenceDialogOpen}
          onClose={() => setGeofenceDialogOpen(false)}
          value={{
            geofenceType: tempGeofence.geofenceType,
            latitude: tempGeofence.centerLatitude,
            longitude: tempGeofence.centerLongitude,
            radiusFeet: tempGeofence.radiusFeet,
            durationMinutes: tempGeofence.durationMinutes,
            polygonCoordinates: tempGeofence.polygonCoordinates,
          }}
          onChange={(newValue) => setTempGeofence({
            geofenceType: newValue.geofenceType,
            centerLatitude: newValue.latitude,
            centerLongitude: newValue.longitude,
            radiusFeet: newValue.radiusFeet,
            durationMinutes: newValue.durationMinutes,
            polygonCoordinates: newValue.polygonCoordinates,
          })}
          onSave={() => {
            onChange({
              ...config,
              geofenceType: tempGeofence.geofenceType,
              // Circle geofence fields
              centerLatitude: tempGeofence.centerLatitude,
              centerLongitude: tempGeofence.centerLongitude,
              radiusFeet: tempGeofence.radiusFeet,
              radiusUnit: 'feet',
              // Polygon geofence fields
              polygonCoordinates: tempGeofence.polygonCoordinates,
              // Common fields
              durationMinutes: tempGeofence.durationMinutes,
            })
            setGeofenceDialogOpen(false)
          }}
          measurementUnit={measurementUnit}
        />
      </Box>
    )
  }

  // User Interests tag selection - API schema: requiredInterests (array of tag IDs/names), requiresAllInterests (boolean, default false)
  const renderUserInterests = () => {
    // API uses requiredInterests (array) and requiresAllInterests (boolean)
    const requiredInterests = (config.requiredInterests as string[]) ?? []
    const requiresAllInterests = (config.requiresAllInterests as boolean) ?? false
    const interestCategories = getCategoriesByType(TagType.Interests)

    // Build map of selected tags for display
    const selectedTagsMap = new Map<string, { id: string; name: string; categoryColor?: string }>()
    interestCategories.forEach((category) => {
      category.tags?.forEach((tag) => {
        if (requiredInterests.includes(tag.id)) {
          selectedTagsMap.set(tag.id, {
            id: tag.id,
            name: tag.displayName || tag.name,
            categoryColor: category.color,
          })
        }
      })
    })

    const handleToggleTag = (tagId: string) => {
      const newSelectedIds = requiredInterests.includes(tagId)
        ? requiredInterests.filter((id) => id !== tagId)
        : [...requiredInterests, tagId]
      onChange({ ...config, requiredInterests: newSelectedIds })
    }

    const handleRemoveTag = (tagId: string) => {
      onChange({ ...config, requiredInterests: requiredInterests.filter((id) => id !== tagId) })
    }

    if (isLoadingTags) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading tags...
          </Typography>
        </Box>
      )
    }

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Required User Interests
        </Typography>

        {/* Match mode selector - API uses requiresAllInterests boolean */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip
            label="Match Any (OR)"
            onClick={() => onChange({ ...config, requiresAllInterests: false })}
            color={!requiresAllInterests ? 'primary' : 'default'}
            variant={!requiresAllInterests ? 'filled' : 'outlined'}
            size="small"
          />
          <Chip
            label="Match All (AND)"
            onClick={() => onChange({ ...config, requiresAllInterests: true })}
            color={requiresAllInterests ? 'primary' : 'default'}
            variant={requiresAllInterests ? 'filled' : 'outlined'}
            size="small"
          />
        </Box>

        {/* Selected tags summary */}
        {selectedTagsMap.size > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
              Selected ({selectedTagsMap.size}):
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {Array.from(selectedTagsMap.values()).map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  size="small"
                  onDelete={() => handleRemoveTag(tag.id)}
                  sx={{
                    bgcolor: tag.categoryColor ? `${tag.categoryColor}20` : undefined,
                    borderColor: tag.categoryColor,
                    borderWidth: tag.categoryColor ? 1 : 0,
                    borderStyle: 'solid',
                  }}
                />
              ))}
            </Box>
          </Paper>
        )}

        {/* Category accordions */}
        {interestCategories.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No interest categories available
          </Alert>
        ) : (
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {interestCategories.map((category) => (
              <Accordion
                key={category.id}
                expanded={expandedInterestCategory === category.id}
                onChange={(_, expanded) => setExpandedInterestCategory(expanded ? category.id : null)}
                sx={{ '&:before': { display: 'none' } }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: category.color ? `${category.color}10` : 'action.hover',
                    borderLeft: category.color ? `4px solid ${category.color}` : undefined,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {category.icon && <Typography>{category.icon}</Typography>}
                    <Typography variant="body2" fontWeight={500}>
                      {category.displayName || category.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({category.tags?.filter((t) => requiredInterests.includes(t.id)).length || 0}/{category.tags?.length || 0})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 1 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {category.tags?.map((tag) => (
                      <Chip
                        key={tag.id}
                        label={tag.displayName || tag.name}
                        size="small"
                        onClick={() => handleToggleTag(tag.id)}
                        color={requiredInterests.includes(tag.id) ? 'primary' : 'default'}
                        variant={requiredInterests.includes(tag.id) ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          User must have {requiresAllInterests ? 'all' : 'at least one'} of the selected interests
        </Typography>
      </Box>
    )
  }

  // User Gender Identity tag selection - API schema: requiredGenderIdentities (array of tag IDs)
  const renderUserGenderIdentity = () => {
    // API uses requiredGenderIdentities (array of tag IDs)
    const requiredGenderIdentities = (config.requiredGenderIdentities as string[]) ?? []
    const genderCategories = getCategoriesByType(TagType.GenderIdentity)

    // Build map of selected tags for display
    const selectedTagsMap = new Map<string, { id: string; name: string; categoryColor?: string }>()
    genderCategories.forEach((category) => {
      category.tags?.forEach((tag) => {
        if (requiredGenderIdentities.includes(tag.id)) {
          selectedTagsMap.set(tag.id, {
            id: tag.id,
            name: tag.displayName || tag.name,
            categoryColor: category.color,
          })
        }
      })
    })

    const handleToggleTag = (tagId: string) => {
      const newSelectedIds = requiredGenderIdentities.includes(tagId)
        ? requiredGenderIdentities.filter((id) => id !== tagId)
        : [...requiredGenderIdentities, tagId]
      onChange({ ...config, requiredGenderIdentities: newSelectedIds })
    }

    const handleRemoveTag = (tagId: string) => {
      onChange({ ...config, requiredGenderIdentities: requiredGenderIdentities.filter((id) => id !== tagId) })
    }

    if (isLoadingTags) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading tags...
          </Typography>
        </Box>
      )
    }

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Allowed Gender Identities
        </Typography>

        {/* Selected tags summary */}
        {selectedTagsMap.size > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
              Selected ({selectedTagsMap.size}):
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {Array.from(selectedTagsMap.values()).map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  size="small"
                  onDelete={() => handleRemoveTag(tag.id)}
                  sx={{
                    bgcolor: tag.categoryColor ? `${tag.categoryColor}20` : undefined,
                    borderColor: tag.categoryColor,
                    borderWidth: tag.categoryColor ? 1 : 0,
                    borderStyle: 'solid',
                  }}
                />
              ))}
            </Box>
          </Paper>
        )}

        {/* Category accordions */}
        {genderCategories.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No gender identity categories available
          </Alert>
        ) : (
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {genderCategories.map((category) => (
              <Accordion
                key={category.id}
                expanded={expandedGenderCategory === category.id}
                onChange={(_, expanded) => setExpandedGenderCategory(expanded ? category.id : null)}
                sx={{ '&:before': { display: 'none' } }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: category.color ? `${category.color}10` : 'action.hover',
                    borderLeft: category.color ? `4px solid ${category.color}` : undefined,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {category.icon && <Typography>{category.icon}</Typography>}
                    <Typography variant="body2" fontWeight={500}>
                      {category.displayName || category.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({category.tags?.filter((t) => requiredGenderIdentities.includes(t.id)).length || 0}/{category.tags?.length || 0})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 1 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {category.tags?.map((tag) => (
                      <Chip
                        key={tag.id}
                        label={tag.displayName || tag.name}
                        size="small"
                        onClick={() => handleToggleTag(tag.id)}
                        color={requiredGenderIdentities.includes(tag.id) ? 'primary' : 'default'}
                        variant={requiredGenderIdentities.includes(tag.id) ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          User's gender identity must match one of the selected options
        </Typography>
      </Box>
    )
  }

  // User Rank Range selector - API schema: minRankTier (1-10), maxRankTier (1-10)
  const renderUserRank = () => {
    // API uses minRankTier/maxRankTier
    const minRankTier = (config.minRankTier as number) ?? 1
    const maxRankTier = (config.maxRankTier as number) ?? 10

    const minRankData = USER_RANKS.find((r) => r.tier === minRankTier) ?? USER_RANKS[0]
    const maxRankData = USER_RANKS.find((r) => r.tier === maxRankTier) ?? USER_RANKS[USER_RANKS.length - 1]

    // Generate slider marks with rank badges
    const marks = USER_RANKS.map((rank) => ({
      value: rank.tier,
      label: rank.badge,
    }))

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Allowed Rank Range
        </Typography>

        {/* Display selected range info */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            {/* Min Rank */}
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Minimum
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 0.5 }}>
                <Typography variant="h5">{minRankData.badge}</Typography>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {minRankData.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tier {minRankData.tier} · {minRankData.pointsRequired.toLocaleString()} pts
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Typography variant="h6" color="text.secondary">
              →
            </Typography>

            {/* Max Rank */}
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Maximum
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 0.5 }}>
                <Typography variant="h5">{maxRankData.badge}</Typography>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {maxRankData.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tier {maxRankData.tier} · {maxRankData.pointsRequired.toLocaleString()} pts
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Dual-point slider */}
        <Box sx={{ px: 1 }}>
          <Slider
            value={[minRankTier, maxRankTier]}
            onChange={(_e, v) => {
              const [min, max] = v as number[]
              onChange({ ...config, minRankTier: min, maxRankTier: max })
            }}
            min={1}
            max={10}
            step={1}
            marks={marks}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => {
              const rank = USER_RANKS.find((r) => r.tier === v)
              return rank ? `${rank.badge} ${rank.name}` : `Tier ${v}`
            }}
            sx={{
              '& .MuiSlider-markLabel': {
                fontSize: '1.2rem',
              },
              '& .MuiSlider-thumb': {
                width: 24,
                height: 24,
              },
            }}
          />
        </Box>

        {/* Rank descriptions */}
        {minRankTier === maxRankTier ? (
          <Paper variant="outlined" sx={{ mt: 2, p: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {minRankData.description}
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Users with ranks from <strong>{minRankData.name}</strong> ({minRankData.pointsRequired.toLocaleString()} pts)
              to <strong>{maxRankData.name}</strong> ({maxRankData.pointsRequired.toLocaleString()} pts) can access this adventure.
            </Typography>
          </Box>
        )}

        {/* Quick select buttons */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
          <Chip
            label="All Ranks"
            size="small"
            variant={minRankTier === 1 && maxRankTier === 10 ? 'filled' : 'outlined'}
            color={minRankTier === 1 && maxRankTier === 10 ? 'primary' : 'default'}
            onClick={() => onChange({ ...config, minRankTier: 1, maxRankTier: 10 })}
          />
          <Chip
            label="Beginners (1-3)"
            size="small"
            variant={minRankTier === 1 && maxRankTier === 3 ? 'filled' : 'outlined'}
            color={minRankTier === 1 && maxRankTier === 3 ? 'primary' : 'default'}
            onClick={() => onChange({ ...config, minRankTier: 1, maxRankTier: 3 })}
          />
          <Chip
            label="Intermediate (4-6)"
            size="small"
            variant={minRankTier === 4 && maxRankTier === 6 ? 'filled' : 'outlined'}
            color={minRankTier === 4 && maxRankTier === 6 ? 'primary' : 'default'}
            onClick={() => onChange({ ...config, minRankTier: 4, maxRankTier: 6 })}
          />
          <Chip
            label="Advanced (7-10)"
            size="small"
            variant={minRankTier === 7 && maxRankTier === 10 ? 'filled' : 'outlined'}
            color={minRankTier === 7 && maxRankTier === 10 ? 'primary' : 'default'}
            onClick={() => onChange({ ...config, minRankTier: 7, maxRankTier: 10 })}
          />
          <Chip
            label="Masters Only (10)"
            size="small"
            variant={minRankTier === 10 && maxRankTier === 10 ? 'filled' : 'outlined'}
            color={minRankTier === 10 && maxRankTier === 10 ? 'primary' : 'default'}
            onClick={() => onChange({ ...config, minRankTier: 10, maxRankTier: 10 })}
          />
        </Box>
      </Box>
    )
  }

  // Celestial Solar Noon - API schema: toleranceMinutes
  const renderCelestialSolarNoon = () => {
    const toleranceMinutes = (config.toleranceMinutes as number) ?? 30

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Solar Noon Tolerance: ±{toleranceMinutes} minutes
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Condition passes when within this many minutes of solar noon (when the sun is at its highest point).
        </Typography>
        <Slider
          value={toleranceMinutes}
          onChange={(_e, v) => onChange({ ...config, toleranceMinutes: v as number })}
          min={5}
          max={120}
          step={5}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `±${v} min`}
          marks={[
            { value: 5, label: '5m' },
            { value: 30, label: '30m' },
            { value: 60, label: '1h' },
            { value: 120, label: '2h' },
          ]}
        />
        {/* Quick select */}
        <Box sx={{ display: 'flex', gap: 0.5, mt: 2, flexWrap: 'wrap' }}>
          {[15, 30, 45, 60].map((mins) => (
            <Chip
              key={mins}
              label={`±${mins}m`}
              size="small"
              variant={toleranceMinutes === mins ? 'filled' : 'outlined'}
              color={toleranceMinutes === mins ? 'primary' : 'default'}
              onClick={() => onChange({ ...config, toleranceMinutes: mins })}
            />
          ))}
        </Box>
      </Box>
    )
  }

  // Celestial Twilight - API schema: twilightType, isDawn, timing, minutesBefore, minutesAfter, durationMinutes
  const renderCelestialTwilight = () => {
    const twilightType = (config.twilightType as number) ?? 0 // 0=Civil, 1=Nautical, 2=Astronomical
    const isDawn = (config.isDawn as boolean) ?? true
    const timing = (config.timing as string) ?? 'during'
    const durationMinutes = (config.durationMinutes as number) ?? 60

    const twilightTypes = [
      { value: 0, label: 'Civil', description: 'Sun 0-6° below horizon' },
      { value: 1, label: 'Nautical', description: 'Sun 6-12° below horizon' },
      { value: 2, label: 'Astronomical', description: 'Sun 12-18° below horizon' },
    ]

    return (
      <Box>
        {/* Twilight type */}
        <Typography variant="subtitle2" gutterBottom>
          Twilight Type
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 3, flexWrap: 'wrap' }}>
          {twilightTypes.map((t) => (
            <Chip
              key={t.value}
              label={t.label}
              size="small"
              variant={twilightType === t.value ? 'filled' : 'outlined'}
              color={twilightType === t.value ? 'primary' : 'default'}
              onClick={() => onChange({ ...config, twilightType: t.value })}
            />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {twilightTypes.find((t) => t.value === twilightType)?.description}
        </Typography>

        {/* Dawn/Dusk selection */}
        <Typography variant="subtitle2" gutterBottom>
          Time of Day
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 3 }}>
          <Chip
            label="🌅 Dawn"
            size="small"
            variant={isDawn ? 'filled' : 'outlined'}
            color={isDawn ? 'primary' : 'default'}
            onClick={() => onChange({ ...config, isDawn: true })}
          />
          <Chip
            label="🌆 Dusk"
            size="small"
            variant={!isDawn ? 'filled' : 'outlined'}
            color={!isDawn ? 'primary' : 'default'}
            onClick={() => onChange({ ...config, isDawn: false })}
          />
        </Box>

        {/* Timing */}
        <Typography variant="subtitle2" gutterBottom>
          Timing
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 3 }}>
          {['before', 'during', 'after'].map((t) => (
            <Chip
              key={t}
              label={t.charAt(0).toUpperCase() + t.slice(1)}
              size="small"
              variant={timing === t ? 'filled' : 'outlined'}
              color={timing === t ? 'primary' : 'default'}
              onClick={() => onChange({ ...config, timing: t })}
            />
          ))}
        </Box>

        {/* Duration */}
        <Typography variant="subtitle2" gutterBottom>
          Duration Window: {durationMinutes} minutes
        </Typography>
        <Slider
          value={durationMinutes}
          onChange={(_e, v) => onChange({ ...config, durationMinutes: v as number })}
          min={15}
          max={180}
          step={15}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}m`}
          marks={[
            { value: 15, label: '15m' },
            { value: 60, label: '1h' },
            { value: 120, label: '2h' },
            { value: 180, label: '3h' },
          ]}
        />
      </Box>
    )
  }

  // Celestial Moonrise/Moonset - API schema: eventType, timing, minutesBefore, minutesAfter, durationMinutes
  const renderCelestialMoonRiseSet = () => {
    const eventType = (config.eventType as string) ?? 'moonrise'
    const timing = (config.timing as string) ?? 'during'
    const durationMinutes = (config.durationMinutes as number) ?? 60

    return (
      <Box>
        {/* Event type */}
        <Typography variant="subtitle2" gutterBottom>
          Event Type
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 3 }}>
          <Chip
            label="🌕 Moonrise"
            size="small"
            variant={eventType === 'moonrise' ? 'filled' : 'outlined'}
            color={eventType === 'moonrise' ? 'primary' : 'default'}
            onClick={() => onChange({ ...config, eventType: 'moonrise' })}
          />
          <Chip
            label="🌑 Moonset"
            size="small"
            variant={eventType === 'moonset' ? 'filled' : 'outlined'}
            color={eventType === 'moonset' ? 'primary' : 'default'}
            onClick={() => onChange({ ...config, eventType: 'moonset' })}
          />
        </Box>

        {/* Timing */}
        <Typography variant="subtitle2" gutterBottom>
          Timing
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 3 }}>
          {['before', 'during', 'after'].map((t) => (
            <Chip
              key={t}
              label={t.charAt(0).toUpperCase() + t.slice(1)}
              size="small"
              variant={timing === t ? 'filled' : 'outlined'}
              color={timing === t ? 'primary' : 'default'}
              onClick={() => onChange({ ...config, timing: t })}
            />
          ))}
        </Box>

        {/* Duration */}
        <Typography variant="subtitle2" gutterBottom>
          Duration Window: {durationMinutes} minutes
        </Typography>
        <Slider
          value={durationMinutes}
          onChange={(_e, v) => onChange({ ...config, durationMinutes: v as number })}
          min={15}
          max={180}
          step={15}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}m`}
          marks={[
            { value: 15, label: '15m' },
            { value: 60, label: '1h' },
            { value: 120, label: '2h' },
            { value: 180, label: '3h' },
          ]}
        />
      </Box>
    )
  }

  // Celestial Moon Phase - API schema: allowedPhases (bitflags)
  const renderCelestialMoonPhase = () => {
    const allowedPhases = (config.allowedPhases as number) ?? 255

    const phases = [
      { flag: 1, label: 'New Moon', emoji: '🌑' },
      { flag: 2, label: 'Waxing Crescent', emoji: '🌒' },
      { flag: 4, label: 'First Quarter', emoji: '🌓' },
      { flag: 8, label: 'Waxing Gibbous', emoji: '🌔' },
      { flag: 16, label: 'Full Moon', emoji: '🌕' },
      { flag: 32, label: 'Waning Gibbous', emoji: '🌖' },
      { flag: 64, label: 'Last Quarter', emoji: '🌗' },
      { flag: 128, label: 'Waning Crescent', emoji: '🌘' },
    ]

    const togglePhase = (flag: number) => {
      const newValue = allowedPhases ^ flag // XOR to toggle
      onChange({ ...config, allowedPhases: newValue })
    }

    const selectAll = () => onChange({ ...config, allowedPhases: 255 })
    const selectNone = () => onChange({ ...config, allowedPhases: 0 })

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Allowed Moon Phases
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select which moon phases satisfy this condition.
        </Typography>

        {/* Quick select buttons */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
          <Chip label="All" size="small" variant="outlined" onClick={selectAll} />
          <Chip label="None" size="small" variant="outlined" onClick={selectNone} />
          <Chip
            label="New + Full"
            size="small"
            variant={allowedPhases === 17 ? 'filled' : 'outlined'}
            color={allowedPhases === 17 ? 'primary' : 'default'}
            onClick={() => onChange({ ...config, allowedPhases: 17 })}
          />
        </Box>

        {/* Phase grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
          {phases.map((phase) => {
            const isSelected = (allowedPhases & phase.flag) !== 0
            return (
              <Paper
                key={phase.flag}
                variant="outlined"
                sx={{
                  p: 1,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: isSelected ? 'primary.main' : 'background.paper',
                  color: isSelected ? 'primary.contrastText' : 'text.primary',
                  '&:hover': { bgcolor: isSelected ? 'primary.dark' : 'action.hover' },
                }}
                onClick={() => togglePhase(phase.flag)}
              >
                <Typography variant="h5">{phase.emoji}</Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  {phase.label}
                </Typography>
              </Paper>
            )
          })}
        </Box>
      </Box>
    )
  }

  // Celestial Moon Age - API schema: minAge, maxAge (0-29.5 days)
  const renderCelestialMoonAge = () => {
    const minAge = (config.minAge as number) ?? 0
    const maxAge = (config.maxAge as number) ?? 29.5

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Moon Age Range: {minAge.toFixed(1)} - {maxAge.toFixed(1)} days
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The lunar cycle is approximately 29.5 days. New moon is day 0, full moon is around day 14.75.
        </Typography>

        <Slider
          value={[minAge, maxAge]}
          onChange={(_e, v) => {
            const [min, max] = v as number[]
            onChange({ ...config, minAge: min, maxAge: max })
          }}
          min={0}
          max={29.5}
          step={0.5}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v.toFixed(1)}d`}
          marks={[
            { value: 0, label: '🌑 0' },
            { value: 7.4, label: '🌓 7.4' },
            { value: 14.75, label: '🌕 14.75' },
            { value: 22.1, label: '🌗 22.1' },
            { value: 29.5, label: '🌑 29.5' },
          ]}
        />

        {/* Quick select presets */}
        <Box sx={{ display: 'flex', gap: 0.5, mt: 2, flexWrap: 'wrap' }}>
          <Chip
            label="🌑 New Moon (0-2)"
            size="small"
            variant={minAge === 0 && maxAge === 2 ? 'filled' : 'outlined'}
            color={minAge === 0 && maxAge === 2 ? 'primary' : 'default'}
            onClick={() => onChange({ ...config, minAge: 0, maxAge: 2 })}
          />
          <Chip
            label="🌕 Full Moon (13-16)"
            size="small"
            variant={minAge === 13 && maxAge === 16 ? 'filled' : 'outlined'}
            color={minAge === 13 && maxAge === 16 ? 'primary' : 'default'}
            onClick={() => onChange({ ...config, minAge: 13, maxAge: 16 })}
          />
          <Chip
            label="All"
            size="small"
            variant={minAge === 0 && maxAge === 29.5 ? 'filled' : 'outlined'}
            color={minAge === 0 && maxAge === 29.5 ? 'primary' : 'default'}
            onClick={() => onChange({ ...config, minAge: 0, maxAge: 29.5 })}
          />
        </Box>
      </Box>
    )
  }

  // =====================
  // INPUT CRITERIA TYPES
  // =====================

  // QR Code criteria - user must scan or enter the adventure's geocache code
  const renderQRCodeCriteria = () => {
    // Get the geocache code directly from the adventure store (source of truth)
    const adventure = useAdventureEditorStore.getState().adventure
    const geocacheCode = adventure?.geocacheCode || ''

    // Fall back to config values if geocacheCode not available from store
    const customCode = (config.customCode as string) ?? ''
    const expectedQRValue = geocacheCode || (config.expectedQRValue as string) || customCode

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          QR Code Criteria
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Users must scan a QR code or enter the adventure code to proceed.
        </Typography>

        {expectedQRValue ? (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              bgcolor: 'action.hover',
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Adventure Code
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 'bold',
                letterSpacing: 2,
                color: 'primary.main',
              }}
            >
              {expectedQRValue}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              This code is automatically set from the adventure's geocache code
            </Typography>
          </Paper>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No geocache code has been set for this adventure. Please configure one in the Code Management panel.
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary">
          Users can scan the QR code or manually enter this code to satisfy this criteria.
        </Typography>
      </Box>
    )
  }

  // Numeric Input criteria - user must enter a numeric code
  const renderNumericInputCriteria = () => {
    const digitCount = (config.digitCount as number) ?? 4
    const acceptedNumbers = (config.acceptedNumbers as string[]) ?? []

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Numeric Code Criteria
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Users must enter a numeric code to join this adventure.
        </Typography>

        <TextField
          label="Number of Digits"
          type="number"
          value={digitCount}
          onChange={(e) => onChange({ ...config, digitCount: Math.min(6, Math.max(2, parseInt(e.target.value) || 4)) })}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          inputProps={{ min: 2, max: 6 }}
          helperText="2-6 digits"
        />

        <Typography variant="body2" sx={{ mb: 1 }}>
          Accepted Codes ({acceptedNumbers.length})
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {acceptedNumbers.map((code, index) => (
            <Chip
              key={index}
              label={code}
              size="small"
              onDelete={() => {
                const newCodes = acceptedNumbers.filter((_, i) => i !== index)
                onChange({ ...config, acceptedNumbers: newCodes })
              }}
            />
          ))}
        </Box>
        <TextField
          label="Add Code"
          size="small"
          fullWidth
          inputProps={{ maxLength: digitCount }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const input = e.target as HTMLInputElement
              const newCode = input.value.trim()
              if (newCode && !acceptedNumbers.includes(newCode) && /^\d+$/.test(newCode)) {
                onChange({ ...config, acceptedNumbers: [...acceptedNumbers, newCode] })
                input.value = ''
              }
              e.preventDefault()
            }
          }}
          helperText="Press Enter to add (numbers only)"
        />
      </Box>
    )
  }

  // Question/Answer criteria - user must answer a question
  const renderQuestionAnswerCriteria = () => {
    const questionText = (config.questionText as string) ?? ''
    const correctAnswer = (config.correctAnswer as string) ?? ''

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Question & Answer Criteria
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Users must answer a question correctly to join this adventure.
        </Typography>

        <TextField
          label="Question"
          value={questionText}
          onChange={(e) => onChange({ ...config, questionText: e.target.value })}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          multiline
          rows={2}
        />

        <TextField
          label="Correct Answer"
          value={correctAnswer}
          onChange={(e) => onChange({ ...config, correctAnswer: e.target.value })}
          fullWidth
          size="small"
          helperText="Case-insensitive match"
        />
      </Box>
    )
  }

  // Moderator Approval criteria - user needs moderator approval
  const renderModeratorApprovalCriteria = () => {
    const instructions = (config.instructions as string) ?? ''
    const approvalMessage = (config.approvalMessage as string) ?? ''
    const rejectionMessage = (config.rejectionMessage as string) ?? ''

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Moderator Approval Criteria
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Users must receive moderator approval to join this adventure.
        </Typography>

        <TextField
          label="Instructions"
          value={instructions}
          onChange={(e) => onChange({ ...config, instructions: e.target.value })}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          multiline
          rows={2}
          helperText="Instructions shown to users while waiting"
        />

        <TextField
          label="Approval Message"
          value={approvalMessage}
          onChange={(e) => onChange({ ...config, approvalMessage: e.target.value })}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          helperText="Message shown when approved"
        />

        <TextField
          label="Rejection Message"
          value={rejectionMessage}
          onChange={(e) => onChange({ ...config, rejectionMessage: e.target.value })}
          fullWidth
          size="small"
          helperText="Message shown when rejected"
        />
      </Box>
    )
  }

  // Helper to render content inside a read-only wrapper when needed
  const renderWithReadOnlyWrapper = (content: React.ReactNode) => {
    if (isReadOnly) {
      return (
        <Box
          sx={{
            pointerEvents: 'none',
            opacity: 0.7,
            '& .MuiSlider-root': { pointerEvents: 'none' },
            '& .MuiSwitch-root': { pointerEvents: 'none' },
            '& .MuiButton-root': { pointerEvents: 'none' },
            '& .MuiTextField-root': { pointerEvents: 'none' },
            '& .MuiAutocomplete-root': { pointerEvents: 'none' },
            '& .MuiToggleButton-root': { pointerEvents: 'none' },
            '& .MuiChip-root': { pointerEvents: 'none' },
          }}
        >
          {content}
        </Box>
      )
    }
    return content
  }

  // Render based on condition type
  switch (conditionType) {
    case WorkflowConditionType.WeatherTemperature:
      return renderWithReadOnlyWrapper(renderTemperatureRange())
    case WorkflowConditionType.WeatherHumidity:
      return renderWithReadOnlyWrapper(renderHumidityRange())
    case WorkflowConditionType.WeatherDayNight:
      return renderWithReadOnlyWrapper(renderDaytime())
    case WorkflowConditionType.WeatherWind:
      // Legacy combined wind - render both for backwards compatibility
      return renderWithReadOnlyWrapper(
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {renderWindSpeed()}
          {renderWindDirection()}
        </Box>
      )
    case WorkflowConditionType.WeatherWindSpeed:
      return renderWithReadOnlyWrapper(renderWindSpeed())
    case WorkflowConditionType.WeatherWindDirection:
      return renderWithReadOnlyWrapper(renderWindDirection())
    case WorkflowConditionType.WeatherCloudCover:
      return renderWithReadOnlyWrapper(renderCloudCover())
    case WorkflowConditionType.WeatherVisibility:
      return renderWithReadOnlyWrapper(renderVisibility())
    case WorkflowConditionType.WeatherSunsetSunrise:
      return renderWithReadOnlyWrapper(renderSunsetSunrise())
    case WorkflowConditionType.WeatherCondition:
      return renderWithReadOnlyWrapper(renderWeatherCondition())
    case WorkflowConditionType.GroupMembership:
      return renderWithReadOnlyWrapper(renderGroupMembership())
    case WorkflowConditionType.AdventureCollectionCompletion:
      return renderWithReadOnlyWrapper(renderCollectionCompletion())
    case WorkflowConditionType.AdventureCompleted:
      return renderWithReadOnlyWrapper(renderAdventureCompleted())
    case WorkflowConditionType.Geofence:
      return renderWithReadOnlyWrapper(renderGeofence())
    case WorkflowConditionType.TimeSchedule:
      return renderWithReadOnlyWrapper(<TimeScheduleConfigPanel config={config} onChange={onChange} isReadOnly={isReadOnly} />)
    case WorkflowConditionType.UserInterests:
      return renderWithReadOnlyWrapper(renderUserInterests())
    case WorkflowConditionType.UserGenderIdentity:
      return renderWithReadOnlyWrapper(renderUserGenderIdentity())
    case WorkflowConditionType.UserRank:
      return renderWithReadOnlyWrapper(renderUserRank())
    // Celestial conditions
    case WorkflowConditionType.CelestialSolarNoon:
      return renderWithReadOnlyWrapper(renderCelestialSolarNoon())
    case WorkflowConditionType.CelestialTwilight:
      return renderWithReadOnlyWrapper(renderCelestialTwilight())
    case WorkflowConditionType.CelestialMoonRiseSet:
      return renderWithReadOnlyWrapper(renderCelestialMoonRiseSet())
    case WorkflowConditionType.CelestialMoonPhase:
      return renderWithReadOnlyWrapper(renderCelestialMoonPhase())
    case WorkflowConditionType.CelestialMoonAge:
      return renderWithReadOnlyWrapper(renderCelestialMoonAge())
    // Input criteria types
    case WorkflowConditionType.QRCode:
      return renderWithReadOnlyWrapper(renderQRCodeCriteria())
    case WorkflowConditionType.NumericInput:
      return renderWithReadOnlyWrapper(renderNumericInputCriteria())
    case WorkflowConditionType.QuestionAnswer:
      return renderWithReadOnlyWrapper(renderQuestionAnswerCriteria())
    case WorkflowConditionType.ModeratorApproval:
      return renderWithReadOnlyWrapper(renderModeratorApprovalCriteria())
    default:
      return (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Configuration for this condition type is not yet available.
          </Typography>
        </Box>
      )
  }
}

// Location Picker Dialog Component - for weather conditions to set a fixed location
interface LocationPickerDialogProps {
  open: boolean
  onClose: () => void
  latitude: number
  longitude: number
  onSave: (latitude: number, longitude: number) => void
}

function LocationPickerDialog({
  open,
  onClose,
  latitude,
  longitude,
  onSave,
}: LocationPickerDialogProps) {
  const { nodes, edges } = useWorkflowStore()
  const [tempLatitude, setTempLatitude] = useState(latitude)
  const [tempLongitude, setTempLongitude] = useState(longitude)

  // Extract POI locations from workflow nodes for context
  const contextMarkers = useMemo<ContextMarker[]>(() => {
    return nodes
      .filter(node =>
        (node.data.nodeType === WorkflowNodeType.PoiLocation || node.data.rewardType === 'location') &&
        node.data.poiLocationConfig &&
        typeof node.data.poiLocationConfig.latitude === 'number' &&
        typeof node.data.poiLocationConfig.longitude === 'number' &&
        (node.data.poiLocationConfig.latitude !== 0 || node.data.poiLocationConfig.longitude !== 0)
      )
      .map(node => ({
        id: node.id,
        label: node.data.label,
        latitude: node.data.poiLocationConfig!.latitude,
        longitude: node.data.poiLocationConfig!.longitude,
        emoji: node.data.poiLocationConfig!.markerEmoji || '📍',
        markerStyle: node.data.poiLocationConfig!.markerStyle || 'icon',
        circleRadius: node.data.poiLocationConfig!.circleRadius,
        circleColor: node.data.poiLocationConfig!.circleColor,
      }))
  }, [nodes])

  // Extract routes from workflow edges for context
  const contextRoutes = useMemo<ContextRoute[]>(() => {
    return edges
      .filter(edge => edge.data?.isRouteEdge && (edge.data?.routeConfig as any)?.cachedRoute)
      .map(edge => {
        const routeConfig = edge.data!.routeConfig as RouteConfig
        const coordinates = (routeConfig as any).cachedRoute!.coordinates as [number, number][]
        return {
          id: edge.id,
          coordinates,
          color: routeConfig.lineColor || '#9C27B0',
          style: (routeConfig.lineStyle || 'dashed') as ContextRoute['style'],
          opacity: routeConfig.lineOpacity ?? 0.5,
          arrowStyle: (routeConfig.arrowStyle || 'none') as ContextRoute['arrowStyle'],
        }
      })
  }, [edges])

  // Update temp values when props change
  useEffect(() => {
    if (open) {
      setTempLatitude(latitude)
      setTempLongitude(longitude)
    }
  }, [open, latitude, longitude])

  const hasCenter = tempLatitude !== 0 || tempLongitude !== 0

  const handleSave = () => {
    onSave(tempLatitude, tempLongitude)
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Set Location</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Click on the map to set the location, or enter coordinates manually.
        </Typography>

        {/* Map with GeofencePicker (without radius) */}
        <Box sx={{ mb: 2 }}>
          <GeofencePicker
            center={hasCenter ? { latitude: tempLatitude, longitude: tempLongitude } : undefined}
            radiusMiles={0}
            onChange={(location) => {
              setTempLatitude(location.latitude)
              setTempLongitude(location.longitude)
            }}
            height={400}
            showRadius={false}
            contextMarkers={contextMarkers}
            contextRoutes={contextRoutes}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="Latitude"
            type="number"
            size="small"
            value={tempLatitude}
            onChange={(e) => setTempLatitude(parseFloat(e.target.value) || 0)}
            inputProps={{ step: 0.000001 }}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Longitude"
            type="number"
            size="small"
            value={tempLongitude}
            onChange={(e) => setTempLongitude(parseFloat(e.target.value) || 0)}
            inputProps={{ step: 0.000001 }}
            sx={{ flex: 1 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save Location
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Location Source Section - reusable component for weather conditions
interface LocationSourceSectionProps {
  useUserLocation: boolean
  fixedLatitude: number
  fixedLongitude: number
  onUseUserLocationChange: (useUserLocation: boolean) => void
  onLocationChange: (latitude: number, longitude: number) => void
}

function LocationSourceSection({
  useUserLocation,
  fixedLatitude,
  fixedLongitude,
  onUseUserLocationChange,
  onLocationChange,
}: LocationSourceSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false)

  return (
    <Box sx={{ mt: 2 }}>
      <FormControlLabel
        control={
          <Switch
            checked={useUserLocation}
            onChange={(e) => onUseUserLocationChange(e.target.checked)}
          />
        }
        label="Use user's current location"
      />
      {!useUserLocation && (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Latitude"
              type="number"
              size="small"
              value={fixedLatitude}
              onChange={(e) => onLocationChange(parseFloat(e.target.value) || 0, fixedLongitude)}
              inputProps={{ step: 0.000001 }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Longitude"
              type="number"
              size="small"
              value={fixedLongitude}
              onChange={(e) => onLocationChange(fixedLatitude, parseFloat(e.target.value) || 0)}
              inputProps={{ step: 0.000001 }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<MapIcon />}
              onClick={() => setDialogOpen(true)}
              sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
            >
              Set on Map
            </Button>
          </Box>

          <LocationPickerDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            latitude={fixedLatitude}
            longitude={fixedLongitude}
            onSave={(lat, lng) => onLocationChange(lat, lng)}
          />
        </Box>
      )}
    </Box>
  )
}

// Geofence Dialog Component
interface GeofenceDialogProps {
  open: boolean
  onClose: () => void
  value: {
    geofenceType: 'circle' | 'polygon'
    latitude: number
    longitude: number
    radiusFeet: number
    durationMinutes: number
    polygonCoordinates: [number, number][]
  }
  onChange: (value: {
    geofenceType: 'circle' | 'polygon'
    latitude: number
    longitude: number
    radiusFeet: number
    durationMinutes: number
    polygonCoordinates: [number, number][]
  }) => void
  onSave: () => void
  measurementUnit: MeasurementUnit
}

function GeofenceDialog({
  open,
  onClose,
  value,
  onChange,
  onSave,
  measurementUnit,
}: GeofenceDialogProps) {
  const { nodes, edges } = useWorkflowStore()
  const isMetric = measurementUnit === MeasurementUnit.Metric

  // Extract POI locations from workflow nodes for context
  const contextMarkers = useMemo<ContextMarker[]>(() => {
    return nodes
      .filter(node =>
        (node.data.nodeType === WorkflowNodeType.PoiLocation || node.data.rewardType === 'location') &&
        node.data.poiLocationConfig &&
        typeof node.data.poiLocationConfig.latitude === 'number' &&
        typeof node.data.poiLocationConfig.longitude === 'number' &&
        (node.data.poiLocationConfig.latitude !== 0 || node.data.poiLocationConfig.longitude !== 0)
      )
      .map(node => ({
        id: node.id,
        label: node.data.label,
        latitude: node.data.poiLocationConfig!.latitude,
        longitude: node.data.poiLocationConfig!.longitude,
        emoji: node.data.poiLocationConfig!.markerEmoji || '📍',
        markerStyle: node.data.poiLocationConfig!.markerStyle || 'icon',
        circleRadius: node.data.poiLocationConfig!.circleRadius,
        circleColor: node.data.poiLocationConfig!.circleColor,
      }))
  }, [nodes])

  // Extract routes from workflow edges for context
  const contextRoutes = useMemo<ContextRoute[]>(() => {
    return edges
      .filter(edge => edge.data?.isRouteEdge && (edge.data?.routeConfig as any)?.cachedRoute)
      .map(edge => {
        const routeConfig = edge.data!.routeConfig as RouteConfig
        const coordinates = (routeConfig as any).cachedRoute!.coordinates as [number, number][]
        return {
          id: edge.id,
          coordinates,
          color: routeConfig.lineColor || '#9C27B0',
          style: (routeConfig.lineStyle || 'dashed') as ContextRoute['style'],
          opacity: routeConfig.lineOpacity ?? 0.5,
          arrowStyle: (routeConfig.arrowStyle || 'none') as ContextRoute['arrowStyle'],
        }
      })
  }, [edges])

  // Display in feet or meters, stored in feet (10-5280 range)
  const displayRadius = isMetric ? feetToMeters(value.radiusFeet) : value.radiusFeet
  const unit = isMetric ? 'm' : 'ft'
  const sliderMin = isMetric ? feetToMeters(10) : 10
  const sliderMax = isMetric ? feetToMeters(5280) : 5280

  const handleRadiusChange = (_event: Event, newValue: number | number[]) => {
    const radius = newValue as number
    const storedRadius = isMetric ? metersToFeet(radius) : radius
    onChange({ ...value, radiusFeet: storedRadius })
  }

  const hasCenter = value.latitude !== 0 || value.longitude !== 0
  const hasPolygon = value.polygonCoordinates.length >= 3

  // Convert feet to miles for the GeofencePicker component
  const radiusMilesForMap = feetToMiles(value.radiusFeet)

  const handleGeofenceTypeChange = (newType: 'circle' | 'polygon') => {
    onChange({ ...value, geofenceType: newType })
  }

  const handlePolygonChange = (coords: [number, number][]) => {
    onChange({ ...value, polygonCoordinates: coords })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Set Geofence</DialogTitle>
      <DialogContent>
        {/* Geofence Type Toggle */}
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            value={value.geofenceType}
            exclusive
            onChange={(_e, newType) => newType && handleGeofenceTypeChange(newType)}
            size="small"
            fullWidth
          >
            <ToggleButton value="circle">
              <RadioButtonUncheckedIcon sx={{ mr: 1 }} />
              Circle
            </ToggleButton>
            <ToggleButton value="polygon">
              <PentagonIcon sx={{ mr: 1 }} />
              Polygon
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Map with GeofencePicker */}
        <Box sx={{ mb: 2 }}>
          <GeofencePicker
            mode={value.geofenceType}
            center={hasCenter ? { latitude: value.latitude, longitude: value.longitude } : undefined}
            radiusMiles={radiusMilesForMap}
            onChange={(location) => {
              onChange({ ...value, latitude: location.latitude, longitude: location.longitude })
            }}
            polygonCoordinates={value.polygonCoordinates}
            onPolygonChange={handlePolygonChange}
            height={400}
            showRadius={true}
            radiusColor="#1976d2"
            contextMarkers={contextMarkers}
            contextRoutes={contextRoutes}
          />
        </Box>

        {/* Circle mode controls */}
        {value.geofenceType === 'circle' && (
          <>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="Latitude"
                type="number"
                size="small"
                value={value.latitude}
                onChange={(e) => onChange({ ...value, latitude: parseFloat(e.target.value) || 0 })}
                inputProps={{ step: 0.000001 }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Longitude"
                type="number"
                size="small"
                value={value.longitude}
                onChange={(e) => onChange({ ...value, longitude: parseFloat(e.target.value) || 0 })}
                inputProps={{ step: 0.000001 }}
                sx={{ flex: 1 }}
              />
            </Box>

            <Typography variant="subtitle2" gutterBottom>
              Radius: {displayRadius} {unit} {!isMetric && value.radiusFeet >= 5280 && '(1 mile)'}
            </Typography>
            <Slider
              value={displayRadius}
              onChange={handleRadiusChange}
              min={sliderMin}
              max={sliderMax}
              step={isMetric ? 10 : 10}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v} ${unit}`}
              marks={[
                { value: sliderMin, label: `${sliderMin} ${unit}` },
                { value: isMetric ? 150 : 500, label: isMetric ? '150 m' : '500 ft' },
                { value: isMetric ? 800 : 2640, label: isMetric ? '800 m' : '½ mi' },
                { value: sliderMax, label: isMetric ? '1.6 km' : '1 mi' },
              ]}
            />
          </>
        )}

        {/* Polygon mode controls */}
        {value.geofenceType === 'polygon' && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">
                Polygon: {hasPolygon ? `${value.polygonCoordinates.length} vertices` : 'Not drawn'}
              </Typography>
              {hasPolygon && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => handlePolygonChange([])}
                >
                  Clear Polygon
                </Button>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {hasPolygon
                ? 'Click "Clear Polygon" to draw a new shape'
                : 'Use the polygon tool in the map to draw a geofence boundary'}
            </Typography>
          </Box>
        )}

        {/* Duration slider (common to both types) */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Duration: {value.durationMinutes === 0 ? 'Instant' : `${value.durationMinutes} minute${value.durationMinutes !== 1 ? 's' : ''}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Time user must remain in geofence before condition is met
          </Typography>
          <Slider
            value={value.durationMinutes}
            onChange={(_e, v) => onChange({ ...value, durationMinutes: v as number })}
            min={0}
            max={30}
            step={1}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => v === 0 ? 'Instant' : `${v} min`}
            marks={[
              { value: 0, label: 'Instant' },
              { value: 5, label: '5' },
              { value: 10, label: '10' },
              { value: 15, label: '15' },
              { value: 20, label: '20' },
              { value: 30, label: '30 min' },
            ]}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onSave}>
          Save Geofence
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConditionConfigPanel
